import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import {
  fetchDpDashboard,
  fetchDpNotificacoes,
  marcarNotificacaoLida,
  verificarAlertasDp,
} from './api'
import { formatDate } from './utils'

function prioTone(p?: string) {
  const s = (p || '').toLowerCase()
  if (/(crit|alta|high)/.test(s)) return 'inativo' as const
  if (/(media|méd|warn)/.test(s)) return 'pendente' as const
  return 'ativo' as const
}

export function RotinaPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('dpc')
  const qc = useQueryClient()

  const dash = useQuery({ queryKey: ['dp', 'dashboard'], queryFn: fetchDpDashboard })
  const notifs = useQuery({
    queryKey: ['dp', 'rotina', 'notificacoes'],
    queryFn: () => fetchDpNotificacoes({ nao_lidas: true }),
  })

  const verificarMut = useMutation({
    mutationFn: verificarAlertasDp,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dp', 'rotina'] }),
        qc.invalidateQueries({ queryKey: ['dp', 'dashboard'] }),
      ])
    },
  })

  const lerMut = useMutation({
    mutationFn: marcarNotificacaoLida,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['dp', 'rotina'] })
    },
  })

  const lista = notifs.data?.dados || notifs.data?.data || []
  const colab = dash.data?.colaboradores || {}

  return (
    <section className="hi-page">
      <PageHeader
        title="Rotina trabalhista (DPC)"
        subtitle="Checklist operacional: alertas, prazos e atalhos para cálculo CLT."
        actions={
          <>
            {editable ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={verificarMut.isPending}
                onClick={() => verificarMut.mutate()}
              >
                Gerar alertas
              </button>
            ) : null}
            <a className="btn btn-secondary" href={legacyAppUrl('#/dpc')}>
              Central legado
            </a>
          </>
        }
      />

      {verificarMut.isError ? (
        <Alert tone="danger">
          {verificarMut.error instanceof ApiError
            ? verificarMut.error.message
            : 'Falha ao gerar alertas.'}
        </Alert>
      ) : null}
      {verificarMut.isSuccess ? (
        <Alert tone="success">
          {verificarMut.data?.criadas ?? 0} alerta(s) de férias gerado(s).
        </Alert>
      ) : null}

      <KpiGrid>
        <Kpi label="Ativos" value={String(colab.ativos ?? '—')} />
        <Kpi label="Férias pendentes" value={String(dash.data?.ferias_pendentes ?? '—')} />
        <Kpi label="Férias 60d" value={String(dash.data?.ferias_vencendo_60 ?? '—')} />
        <Kpi label="Rescisões abertas" value={String(dash.data?.rescisoes_abertas ?? '—')} />
        <Kpi label="Alertas críticos" value={String(dash.data?.notificacoes_criticas ?? '—')} />
        <Kpi label="Pendentes na fila" value={String(notifs.data?.nao_lidas ?? lista.length)} />
      </KpiGrid>

      <Section title="Atalhos DPC">
        <div className="hi-filters" style={{ flexWrap: 'wrap' }}>
          <Link className="btn btn-secondary" to="/app/dp/ferias">
            Férias
          </Link>
          <Link className="btn btn-secondary" to="/app/dp/rescisao">
            Rescisão
          </Link>
          <Link className="btn btn-secondary" to="/app/dp">
            Folha
          </Link>
          <Link className="btn btn-secondary" to="/app/dp/massa">
            Cálculo em massa
          </Link>
          <Link className="btn btn-secondary" to="/app/dp/experiencia">
            Experiência
          </Link>
          <Link className="btn btn-secondary" to="/app/dp/admissao">
            Admissão
          </Link>
        </div>
      </Section>

      <Section title="Fila de alertas" flush>
        {notifs.isLoading ? <Muted>Carregando…</Muted> : null}
        {notifs.isError ? (
          <Alert tone="danger">
            {notifs.error instanceof ApiError
              ? notifs.error.message
              : 'Falha ao listar notificações.'}
          </Alert>
        ) : null}
        {!notifs.isLoading && lista.length === 0 ? (
          <Empty>Nenhum alerta pendente. Use “Gerar alertas” para varrer férias vencendo.</Empty>
        ) : null}
        {lista.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Alerta</th>
                <th>Colaborador</th>
                <th>Prioridade</th>
                <th>Prazo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((n) => (
                <tr key={n.id}>
                  <td>
                    <strong>{n.titulo || n.tipo || 'Alerta'}</strong>
                    {n.mensagem ? (
                      <div>
                        <Muted>{n.mensagem}</Muted>
                      </div>
                    ) : null}
                  </td>
                  <td>{n.colaborador_nome || '—'}</td>
                  <td>
                    <StatusBadge tone={prioTone(n.prioridade)}>
                      {n.prioridade || n.status || '—'}
                    </StatusBadge>
                  </td>
                  <td>{formatDate(n.data_prazo || n.criado_em)}</td>
                  <td>
                    {editable && !n.lida ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={lerMut.isPending}
                        onClick={() => lerMut.mutate(n.id)}
                      >
                        Marcar lida
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}

export default RotinaPage
